import { Component } from '@theme/component';

/**
 * A custom element that toggles the muted state of a background video.
 *
 * The video autoplays muted (browser policy). Clicking the button when muted
 * unmutes the video, restarts it from the beginning, and plays it. Clicking it
 * when unmuted re-mutes the video.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The mute/unmute toggle button.
 *
 * @extends Component<Refs>
 */
export class VideoSoundToggle extends Component {
  requiredRefs = ['button'];

  /**
   * Toggles the muted state of the background video.
   * Unmuting restarts playback from the start.
   */
  toggle() {
    const video = this.querySelector('video');
    if (!video) return;

    const { button } = this.refs;

    if (video.muted) {
      video.muted = false;
      video.currentTime = 0;
      video.play().catch(() => {});
      this.dataset.muted = 'false';
    } else {
      video.muted = true;
      this.dataset.muted = 'true';
    }

    const label = video.muted ? button.dataset.labelUnmute : button.dataset.labelMute;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('video-sound-toggle')) {
  customElements.define('video-sound-toggle', VideoSoundToggle);
}
